<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Comeback Offers</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Track abandoned quotes and recovery attempts</p>
      </div>
    </div>

    <!-- Date Range Filter -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
        <span class="text-body-2 font-weight-medium text-medium-emphasis mr-4">Date Range:</span>

        <v-menu v-model="menuStart" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(startDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="startDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <span class="text-body-2 text-medium-emphasis">to</span>

        <v-menu v-model="menuEnd" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(endDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="endDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <v-divider vertical class="mx-2 d-none d-sm-block" style="height: 24px;"></v-divider>

        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          density="compact"
          variant="outlined"
          divided
          rounded="lg"
        >
          <v-btn value="7" size="small" @click="setLastDays(7)">7 Days</v-btn>
          <v-btn value="30" size="small" @click="setLastDays(30)">30 Days</v-btn>
          <v-btn value="90" size="small" @click="setLastDays(90)">90 Days</v-btn>
          <v-btn value="all" size="small" @click="setFromBeginning">From the beginning</v-btn>
        </v-btn-toggle>
      </div>
    </v-card>

    <!-- Summary Stats -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Total Sent</p>
              <h3 class="text-h4 font-weight-bold">{{ stepStats.totalOffers }}</h3>
            </div>
            <v-avatar color="warning" variant="tonal" size="56">
              <v-icon size="28">mdi-email-fast-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="warning" size="18" class="mr-1">mdi-tag-heart-outline</v-icon>
            <span class="text-caption text-medium-emphasis">Comeback offers created</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Clicked</p>
              <h3 class="text-h4 font-weight-bold">{{ stepStats.clickedOffers }}</h3>
            </div>
            <v-avatar color="info" variant="tonal" size="56">
              <v-icon size="28">mdi-cursor-default-click-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-chip size="x-small" color="info" variant="flat" class="mr-2">
              {{ stepStats.totalOffers > 0 ? ((stepStats.clickedOffers / stepStats.totalOffers) * 100).toFixed(1) : 0 }}%
            </v-chip>
            <span class="text-caption text-medium-emphasis">of total sent</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Converted to Quote</p>
              <h3 class="text-h4 font-weight-bold">{{ stepStats.convertedToQuote }}</h3>
            </div>
            <v-avatar color="success" variant="tonal" size="56">
              <v-icon size="28">mdi-check-decagram-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-chip size="x-small" color="success" variant="flat" class="mr-2">
              {{ stepStats.totalOffers > 0 ? ((stepStats.convertedToQuote / stepStats.totalOffers) * 100).toFixed(1) : 0 }}%
            </v-chip>
            <span class="text-caption text-medium-emphasis">of total sent</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Converted to Order</p>
              <h3 class="text-h4 font-weight-bold">{{ fromComebackCount }}</h3>
            </div>
            <v-avatar color="deep-purple" variant="tonal" size="56">
              <v-icon size="28">mdi-cart-check</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-chip size="x-small" color="deep-purple" variant="flat" class="mr-2">
              {{ stepStats.totalOffers > 0 ? ((fromComebackCount / stepStats.totalOffers) * 100).toFixed(1) : 0 }}%
            </v-chip>
            <span class="text-caption text-medium-emphasis">of total sent</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Abandoned Step Breakdown -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="pa-4">
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon class="mr-2" color="primary">mdi-debug-step-over</v-icon>
            Abandoned at Step
          </v-card-title>
          <v-card-text class="pa-0">
            <v-table density="compact">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Count</th>
                  <th>% of Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="step in stepStats.steps" :key="step.stepName">
                  <td class="font-weight-medium">{{ step.stepName }}</td>
                  <td class="font-weight-bold">{{ step.count }}</td>
                  <td class="text-medium-emphasis">
                    {{ stepStats.totalOffers > 0 ? ((step.count / stepStats.totalOffers) * 100).toFixed(1) : 0 }}%
                  </td>
                  <td style="width: 200px;">
                    <v-progress-linear
                      :model-value="stepStats.maxCount > 0 ? (step.count / stepStats.maxCount) * 100 : 0"
                      color="warning"
                      height="6"
                      rounded
                    ></v-progress-linear>
                  </td>
                </tr>
                <tr v-if="!stepStats.steps.length && !statsLoading">
                  <td colspan="4" class="text-center text-medium-emphasis py-4">No data available</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>


    <!-- Comeback Offers Timeline -->
    <v-row class="mb-6">
      <v-col cols="12" lg="8">
        <ComebackOffersTimelineApex :start-date="startDate" :end-date="endDate" />
      </v-col>
      <v-col cols="12" lg="4">
        <ComebackOffersTable :start-date="startDate" :end-date="endDate" />
      </v-col>
    </v-row>

    <!-- Abandoned Steps Monthly Trend -->
    <v-row class="mb-6">
      <v-col cols="12" lg="8">
        <AbandonedStepsTimelineChart :start-date="startDate" :end-date="endDate" />
      </v-col>
    </v-row>

    <!-- Abandoned Steps vs Quotes Received -->
    <v-row class="mb-6">
      <v-col cols="12" lg="8">
        <AbandonedVsQuotesTimelineChart :start-date="startDate" :end-date="endDate" />
      </v-col>
    </v-row>

    <!-- Converted Quotes Table -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2 pa-4">
            <v-icon class="mr-2" color="success">mdi-check-decagram-outline</v-icon>
            Quotes Converted from Comeback Offers
          </v-card-title>
          <v-data-table-server
            v-model:page="convertedPage"
            v-model:items-per-page="convertedItemsPerPage"
            :headers="convertedHeaders"
            :items="convertedQuotes"
            :items-length="convertedTotal"
            :items-per-page-options="[10, 20, 50]"
            :loading="convertedLoading"
            @update:options="loadConvertedItems"
          >
            <template #item.customer="{ item }">
              {{ item.customer?.name }} {{ item.customer?.lastName }}
            </template>
            <template #item.emailAndPhone="{ item }">
              {{ item.customer?.email }}<br />
              {{ item.customer?.phone }}
            </template>
            <template #item.eventDate="{ item }">
              {{ item.date }}
            </template>
            <template #item.createdDate="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template #item.channel="{ item }">
              <v-chip :color="item.channel === 0 ? 'primary' : item.channel === 1 ? 'green' : 'purple'">
                {{ item.channelName || '' }}
              </v-chip>
            </template>
            <template #item.comebackOfferId="{ item }">
              <v-chip v-if="item.comebackOfferId" color="warning" size="small">
                {{ item.comebackOfferId }}
              </v-chip>
            </template>
          </v-data-table-server>
          <div v-if="!convertedQuotes.length && !convertedLoading" class="text-center py-8 text-medium-emphasis">
            No converted quotes found.
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <v-card class="mb-6 pa-4">
      <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
        All sent CB offers
      </v-card-title>
      <div class="d-flex align-center">
        <v-text-field
          v-model="search"
          label="Search by name, last name, email, or phone"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 450px;"
          clearable
        ></v-text-field>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card>
      <v-data-table-server
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      :headers="headers"
      :items="offers"
      :items-length="total"
      :items-per-page-options="[20, 50, 100]"
      :loading="loading"
      :search="search"
      @update:options="loadItems"
    >
      <template #item.view_count="{ item }">
        <v-chip :color="item.view_count === 0 ? '' : 'green'">
          {{ item.view_count }}
        </v-chip>
      </template>
      <template #item.actions="{ item }">
        <div class="text-center">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" density="compact" icon="mdi-dots-horizontal"></v-btn>
            </template>
            <v-list>
              <v-list-item @click="confirmDelete(item)">
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
      <template #item.createdAt="{ item }">
        <span>{{ formatDate(item.createdAt) }}</span>
      </template>
      <template #item.delivery="{ item }">
        {{ formatDelivery(item.delivery) }}
      </template>
      <template #item.channel="{ item }">
        <v-chip :color="item.channel === 0 ? 'primary' : item.channel === 1 ? 'green' : 'purple'">
          {{ item.channelInfo?.name || item.channelName || '' }}
        </v-chip>
      </template>
      </v-data-table-server>
      <div v-if="!offers.length && !loading" class="text-center py-8 text-medium-emphasis">
        No comeback offers found.
      </div>
    </v-card>

    <!-- Abandonment breakdowns — scoped to the same date range as the rest of the page -->
    <div class="mt-6">
      <ComebackBreakdownStats :start-date="startDate" :end-date="endDate" />
    </div>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="420px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="text-body-1">
          Are you sure you want to delete this comeback offer? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteOffer">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { comebackService, quoteService, importedOrderService } from '../services';
import { useNotificationStore } from '../stores';
import { useDebounceSearch } from '../composables/useDebounceSearch';
import { formatDelivery } from '../utils';
import ComebackOffersTimelineApex from '../components/ComebackOffersTimelineApex.vue';
import ComebackOffersTable from '../components/ComebackOffersTable.vue';
import AbandonedStepsTimelineChart from '../components/AbandonedStepsTimelineChart.vue';
import AbandonedVsQuotesTimelineChart from '../components/AbandonedVsQuotesTimelineChart.vue';
import ComebackBreakdownStats from '../components/ComebackBreakdownStats.vue';

defineOptions({ name: 'ComebackOffersView' });

const notificationStore = useNotificationStore();

const offers = ref([]);
const search = ref('');
const page = ref(1);
const itemsPerPage = ref(20);
const total = ref(0);
const loading = ref(false);
const statsLoading = ref(false);
const showDeleteConfirm = ref(false);
const offerToDelete = ref(null);
const stepStats = ref({ steps: [], totalOffers: 0, clickedOffers: 0, convertedToQuote: 0, maxCount: 0 });

// Date range — every card, chart and table on this page is scoped to it.
// Defaults to the full history so the page opens on the same all-time view it
// had before the filter existed.
const BEGINNING_OF_DATA = new Date(2025, 7, 18); // August 18th 2025
const startDate = ref(BEGINNING_OF_DATA);
const endDate = ref(new Date());
const selectedPeriod = ref("all");
const menuStart = ref(false);
const menuEnd = ref(false);

function formatDisplay(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function toIsoDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

const fromComebackCount = ref(0);

const convertedQuotes = ref([]);
const convertedPage = ref(1);
const convertedItemsPerPage = ref(10);
const convertedTotal = ref(0);
const convertedLoading = ref(false);

// The charts and summary table watch their date props and reload themselves;
// this only has to refresh what the view owns.
function reloadRangedData() {
  loadStepStats();
  loadFromComebackCount();
  convertedPage.value = 1;
  loadConvertedQuotes(1, convertedItemsPerPage.value);
  page.value = 1;
  loadOffers(1, itemsPerPage.value);
}

function onDateChange() {
  if (startDate.value && endDate.value && startDate.value > endDate.value) {
    endDate.value = startDate.value;
  }
  reloadRangedData();
}

function setLastDays(days) {
  const end = new Date();
  startDate.value = new Date(end.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  endDate.value = end;
  selectedPeriod.value = String(days);
  reloadRangedData();
}

function setFromBeginning() {
  startDate.value = BEGINNING_OF_DATA;
  endDate.value = new Date();
  selectedPeriod.value = 'all';
  reloadRangedData();
}

const convertedHeaders = [
  { title: 'ID', key: 'id' },
  { title: 'Created Date', key: 'createdDate' },
  { title: 'Customer', key: 'customer' },
  { title: 'Email & Phone', key: 'emailAndPhone' },
  { title: 'Event Date', key: 'eventDate' },
  { title: 'Delivery', key: 'delivery' },
  { title: 'Menu Name', key: 'menuName' },
  { title: 'Guests', key: 'numberOfGuests' },
  { title: 'Total Cost', key: 'totalCost' },
  { title: 'Channel', key: 'channel' },
  { title: 'Comeback Id', key: 'comebackOfferId' },
];

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Clicks', key: 'view_count' },
  { title: 'Step', key: 'abandonedStepName' },
  { title: 'Email', key: 'email' },
  { title: 'Name', key: 'name' },
  { title: 'Last Name', key: 'lastName' },
  { title: 'Phone', key: 'phone' },
  { title: 'City', key: 'city' },
  { title: 'Date', key: 'date' },
  { title: 'Occasion', key: 'occasion' },
  { title: 'Time', key: 'time' },
  { title: 'Delivery', key: 'delivery' },
  { title: 'Selected Menu', key: 'selectedMenu' },
  { title: 'Guests', key: 'numberOfGuests' },
  { title: 'Special Offer', key: 'selectedSpecialOffer' },
  { title: 'Addons', key: 'selectedAddons' },
  { title: 'Channel', key: 'channel' },
  { title: 'Message', key: 'additionalMessage' },
  { title: 'Actions', key: 'actions', sortable: false },
];

async function loadStepStats() {
  statsLoading.value = true;
  try {
    const response = await comebackService.getAbandonedStepStats({
      startDate: toIsoDate(startDate.value),
      endDate: toIsoDate(endDate.value),
    });
    if (response.success) {
      const steps = response.data.steps;
      const maxCount = steps.length > 0 ? Math.max(...steps.map(s => s.count)) : 0;
      stepStats.value = {
        steps,
        totalOffers: response.data.totalOffers,
        clickedOffers: response.data.clickedOffers,
        convertedToQuote: response.data.convertedToQuote,
        maxCount
      };
    }
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load step statistics');
  } finally {
    statsLoading.value = false;
  }
}

async function loadConvertedQuotes(targetPage = 1, pageSize = 10) {
  convertedLoading.value = true;
  try {
    const response = await quoteService.getQuoteEntries({
      page: targetPage,
      limit: pageSize,
      convertedOnly: true,
      startDate: toIsoDate(startDate.value),
      endDate: toIsoDate(endDate.value),
    });
    convertedQuotes.value = response.data || [];
    convertedTotal.value = response.total || 0;
    convertedPage.value = response.page || targetPage;
    convertedItemsPerPage.value = response.pageSize || pageSize;
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load converted quotes');
    convertedQuotes.value = [];
    convertedTotal.value = 0;
  } finally {
    convertedLoading.value = false;
  }
}

function loadConvertedItems(options) {
  convertedPage.value = options.page;
  convertedItemsPerPage.value = options.itemsPerPage;
  loadConvertedQuotes(convertedPage.value, convertedItemsPerPage.value);
}

async function loadFromComebackCount() {
  try {
    const data = await importedOrderService.list({
      page: 1,
      limit: 1,
      fromComeback: 'true',
      startDate: toIsoDate(startDate.value),
      endDate: toIsoDate(endDate.value),
    });
    fromComebackCount.value = data.total || 0;
  } catch (err) {
    fromComebackCount.value = 0;
  }
}

async function loadOffers(targetPage = 1, pageSize = 20) {
  loading.value = true;
  try {
    const data = await comebackService.list({
      page: targetPage,
      limit: pageSize,
      search: search.value,
      startDate: toIsoDate(startDate.value),
      endDate: toIsoDate(endDate.value),
    });
    offers.value = data.data;
    total.value = data.total;
    page.value = data.page || targetPage;
    itemsPerPage.value = pageSize;
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load comeback offers');
  } finally {
    loading.value = false;
  }
}

useDebounceSearch(search, () => {
  page.value = 1;
  loadOffers(1, itemsPerPage.value);
});

function confirmDelete(item) {
  offerToDelete.value = item;
  showDeleteConfirm.value = true;
}

async function deleteOffer() {
  if (!offerToDelete.value) return;
  try {
    await comebackService.delete(offerToDelete.value.id);
    showDeleteConfirm.value = false;
    offerToDelete.value = null;
    notificationStore.showSuccess('Comeback offer deleted successfully');
    loadOffers(page.value, itemsPerPage.value);
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to delete comeback offer');
  }
}

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
}

function loadItems(options) {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  loadOffers(page.value, itemsPerPage.value);
}

onMounted(() => {
  loadStepStats();
  loadFromComebackCount();
  loadConvertedQuotes(convertedPage.value, convertedItemsPerPage.value);
  loadOffers(page.value, itemsPerPage.value);
});
</script>

<style scoped>
:deep(.v-data-table) {
  border-radius: 0 0 12px 12px;
}

:deep(.v-data-table-header) {
  background-color: #f8fafc;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: #64748b !important;
}
</style>


